import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface CuratorData {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  curator_tagline: string | null;
  curator_bio: string | null;
  follower_count: number;
  is_following: boolean;
}

interface FeedItem {
  id: string;
  title: string | null;
  body: string | null;
  platform: string;
  author: string | null;
  published_at: string | null;
  url: string | null;
}

const PlatformBadge: React.FC<{ platform: string }> = ({ platform }) => {
  const labels: Record<string, string> = {
    reddit: 'Reddit',
    bluesky: 'Bluesky',
    youtube: 'YouTube',
    twitter: 'X',
    rss: 'RSS',
  };
  return (
    <span className="text-[10px] uppercase tracking-wider border border-border rounded-full px-2 py-0.5 text-muted-foreground">
      {labels[platform] ?? platform}
    </span>
  );
};

const AvatarPlaceholder: React.FC<{ name: string | null; size?: number }> = ({ name, size = 56 }) => {
  const initials = (name ?? '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="rounded-full bg-muted flex items-center justify-center text-foreground font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials}
    </div>
  );
};

export const CuratorProfile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { user } = useAuth();

  const [curator, setCurator] = useState<CuratorData | null>(null);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [loadingCurator, setLoadingCurator] = useState(true);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // ── fetch curator profile ──────────────────────────────────────────────────
  useEffect(() => {
    if (!username) return;
    setLoadingCurator(true);

    supabase
      .rpc('get_featured_curators', { p_user_id: user?.id ?? null })
      .then(({ data, error }) => {
        if (error) {
          console.error('get_featured_curators error:', error.message);
          setNotFound(true);
          return;
        }
        // get_featured_curators returns all featured; filter client-side for this username
        // For non-featured curators fall back to a direct profiles query below
        const match = (data as CuratorData[] | null)?.find(
          (c) => c.username === username,
        );
        if (match) {
          setCurator(match);
          setLoadingCurator(false);
          return;
        }
        // Not in featured list — query profiles directly
        supabase
          .from('profiles')
          .select('id, username, full_name, avatar_url, curator_tagline, curator_bio, follower_count')
          .eq('username', username)
          .eq('is_curator', true)
          .eq('feed_is_public', true)
          .maybeSingle()
          .then(({ data: p, error: pe }) => {
            if (pe || !p) { setNotFound(true); setLoadingCurator(false); return; }
            // Check if the current user follows
            supabase
              .from('curator_follows')
              .select('id')
              .eq('follower_id', user?.id ?? '')
              .eq('curator_id', p.id)
              .maybeSingle()
              .then(({ data: f }) => {
                setCurator({ ...(p as CuratorData), is_following: Boolean(f) });
                setLoadingCurator(false);
              });
          });
      });
  }, [username, user?.id]);

  // ── fetch public curated feed items ───────────────────────────────────────
  useEffect(() => {
    if (!curator?.id) return;
    setLoadingFeed(true);
    supabase
      .from('social_feed_items')
      .select('id, title, body, platform, author, published_at, url')
      .eq('user_id', curator.id)
      .eq('is_filtered', false)
      .order('published_at', { ascending: false })
      .limit(40)
      .then(({ data, error }) => {
        if (!error && data) setFeed(data as FeedItem[]);
        setLoadingFeed(false);
      });
  }, [curator?.id]);

  // ── follow / unfollow ──────────────────────────────────────────────────────
  const handleFollow = async () => {
    if (!user) {
      toast.error('Sign in to follow curators.');
      return;
    }
    if (!curator) return;
    setFollowLoading(true);
    try {
      if (curator.is_following) {
        await supabase
          .from('curator_follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('curator_id', curator.id);
        setCurator((c) => c ? { ...c, is_following: false, follower_count: Math.max(c.follower_count - 1, 0) } : c);
      } else {
        await supabase
          .from('curator_follows')
          .insert({ follower_id: user.id, curator_id: curator.id });
        setCurator((c) => c ? { ...c, is_following: true, follower_count: c.follower_count + 1 } : c);
      }
    } catch {
      toast.error('Something went wrong. Try again.');
    } finally {
      setFollowLoading(false);
    }
  };

  // ── render states ─────────────────────────────────────────────────────────
  if (loadingCurator) {
    return (
      <div className="min-h-screen bg-background text-foreground font-sans">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center text-muted-foreground text-sm">
          Loading…
        </div>
      </div>
    );
  }

  if (notFound || !curator) {
    return (
      <div className="min-h-screen bg-background text-foreground font-sans">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <p className="text-muted-foreground text-sm">Curator not found.</p>
          <Link to="/" className="mt-4 inline-block text-sm underline-offset-4 hover:underline">
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  const formattedCount = curator.follower_count.toLocaleString();

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <Header />

      {/* Profile hero */}
      <section className="border-b border-border/60">
        <div className="max-w-2xl mx-auto px-6 py-14 flex flex-col sm:flex-row sm:items-start gap-6">
          {curator.avatar_url ? (
            <img
              src={curator.avatar_url}
              alt={curator.full_name ?? curator.username}
              className="w-14 h-14 rounded-full object-cover shrink-0"
            />
          ) : (
            <AvatarPlaceholder name={curator.full_name} size={56} />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">
                  {curator.full_name ?? `@${curator.username}`}
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">@{curator.username}</p>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm text-muted-foreground tabular-nums">
                  {formattedCount} {curator.follower_count === 1 ? 'follower' : 'followers'}
                </span>
                {user?.id !== curator.id && (
                  <Button
                    size="sm"
                    variant={curator.is_following ? 'outline' : 'default'}
                    onClick={handleFollow}
                    disabled={followLoading}
                  >
                    {followLoading ? '…' : curator.is_following ? 'Following' : 'Follow'}
                  </Button>
                )}
              </div>
            </div>

            {curator.curator_tagline && (
              <p className="mt-3 text-sm font-medium text-foreground/80">
                {curator.curator_tagline}
              </p>
            )}
            {curator.curator_bio && (
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {curator.curator_bio}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Feed */}
      <section className="max-w-2xl mx-auto px-6 py-10">
        <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-8">
          Curated feed
        </h2>

        {loadingFeed ? (
          <p className="text-sm text-muted-foreground">Loading feed…</p>
        ) : feed.length === 0 ? (
          <p className="text-sm text-muted-foreground">No public posts yet.</p>
        ) : (
          <div className="space-y-6">
            {feed.map((item) => (
              <article key={item.id} className="border-b border-border/40 pb-6 last:border-0">
                <div className="flex items-center gap-2 mb-2">
                  <PlatformBadge platform={item.platform} />
                  {item.author && (
                    <span className="text-xs text-muted-foreground">{item.author}</span>
                  )}
                  {item.published_at && (
                    <span className="text-xs text-muted-foreground ml-auto tabular-nums">
                      {new Date(item.published_at).toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric',
                      })}
                    </span>
                  )}
                </div>
                <p className="text-sm leading-relaxed line-clamp-4">{item.title ?? item.body}</p>
                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                  >
                    View original →
                  </a>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>© 2026 SocialScoop.ai — Built for people, not platforms.</div>
          <nav className="flex flex-wrap gap-5">
            <Link to="/features" className="hover:text-foreground transition-colors">Features</Link>
            <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default CuratorProfile;
